// Builders for the JSON-LD blocks that Layout writes into the page head.
import { site, apps, appStoreRating, absoluteUrl } from "../config/site";

const organisationId = `${site.siteUrl}/#organisation`;
const websiteId = `${site.siteUrl}/#website`;

export const organisationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": organisationId,
  name: site.name,
  legalName: site.publisher,
  url: `${site.siteUrl}/`,
  logo: site.socialImageSquare,
  email: "contact@schengensimple.com",
  sameAs: [apps.ios.listingUrl, apps.android.listingUrl],
});

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": websiteId,
  name: site.name,
  url: `${site.siteUrl}/`,
  inLanguage: "en-GB",
  publisher: { "@id": organisationId },
});

const appSchema = ({ operatingSystem, installUrl, withRating }) => ({
  "@context": "https://schema.org",
  "@type": "MobileApplication",
  name: site.name,
  description:
    "Tracks the Schengen 90/180-day rule across past and planned trips, and shows how long you can stay on any date.",
  applicationCategory: "TravelApplication",
  operatingSystem,
  url: `${site.siteUrl}/`,
  installUrl,
  publisher: { "@id": organisationId },
  // The app is free to download; plans are bought inside it.
  offers: { "@type": "Offer", price: "0", priceCurrency: "GBP" },
  ...(withRating && {
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: appStoreRating.value,
      ratingCount: appStoreRating.count,
      bestRating: "5",
    },
  }),
});

// The rating is the App Store's, so only the iOS entry carries it.
export const appSchemas = () => [
  appSchema({
    operatingSystem: "iOS",
    installUrl: apps.ios.listingUrl,
    withRating: true,
  }),
  appSchema({
    operatingSystem: "Android",
    installUrl: apps.android.listingUrl,
    withRating: false,
  }),
];

export const articleSchema = ({
  slug,
  headline,
  description,
  datePublished,
  dateModified,
}) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline,
  description,
  inLanguage: "en-GB",
  mainEntityOfPage: absoluteUrl(slug),
  image: site.socialImageLandscape,
  ...(datePublished && { datePublished }),
  ...(dateModified && { dateModified }),
  author: { "@type": "Organization", name: site.name, url: `${site.siteUrl}/` },
  publisher: {
    "@type": "Organization",
    name: site.name,
    legalName: site.publisher,
    logo: { "@type": "ImageObject", url: site.socialImageSquare },
  },
});

export const guidesHubSlug = "about-the-schengen-area";

export const breadcrumbSchema = ({ slug, name }) => {
  const trail = [
    { name: "Home", url: `${site.siteUrl}/` },
    { name: "Schengen guides", url: absoluteUrl(guidesHubSlug) },
  ];
  if (slug !== guidesHubSlug) {
    trail.push({ name, url: absoluteUrl(slug) });
  }

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
};

// questions: [{ question, answer }], where answer is plain text.
export const faqSchema = (questions) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: questions.map(({ question, answer }) => ({
    "@type": "Question",
    name: question,
    acceptedAnswer: { "@type": "Answer", text: answer },
  })),
});
